import { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Fade } from '@mui/material';
import useHousehold from "src/contexts/household-context";
import useWhiteLabelConfig from "src/contexts/config-context";

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

interface LogoLoaderProps {
  messages?: string[];
}

const LogoLoader = ({ messages }: LogoLoaderProps) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const imageRef = useRef<HTMLImageElement>(null);

  const {
      selectedHousehold,
    } = useHousehold();

  const { professional_group } = useWhiteLabelConfig();

  const stateName = useMemo(() => {
    const stateCode = selectedHousehold?.head_of_household?.household_state_code;
    const state = US_STATES.find(s => s.code === stateCode);
    return state ? state.name : '';
  }, [selectedHousehold?.head_of_household?.household_state_code]);

  const defaultAnalysisSteps = useMemo(() => [
    'Setting up your household...',
    `Evaluating ${stateName} tax thresholds...`,
    `Assessing ${stateName} probate difficulty...`,
    'Reviewing family scenarios...',
    'Looking at net worth and assets...',
  ], [stateName]);

  const analysisSteps = messages || defaultAnalysisSteps;

  useEffect(() => {
    const interval = setInterval(() => {
      setStepIndex((prev) => (prev + 1) % analysisSteps.length);
    }, 1500); // rotate every 3 seconds

    return () => clearInterval(interval);
  }, [analysisSteps.length]);

  useEffect(() => {
    const handleResize = () => {
      if (imageRef.current && professional_group?.app_logo && professional_group?.total_app_logo_multiplier) {
        const { naturalWidth, naturalHeight } = imageRef.current;
        const aspectRatio = naturalWidth / naturalHeight;
        let maxWidth = 180;
        let maxHeight = 30;

        // Adjust maxWidth for aspect ratios
        if (aspectRatio < 2.9) {
          maxHeight = 48;
        }
        if (aspectRatio >= 2.9 && aspectRatio <= 4) {
          maxHeight = 40;
        }
        if (aspectRatio > 4) {
          maxHeight = 35;
        }

        if (naturalWidth > maxWidth || naturalHeight > maxHeight) {
          if (maxWidth / aspectRatio < maxHeight) {
            setDimensions({ width: maxWidth * professional_group?.total_app_logo_multiplier, height: maxWidth / aspectRatio * professional_group?.total_app_logo_multiplier });
          } else {
            setDimensions({
              width: maxHeight * aspectRatio * professional_group?.total_app_logo_multiplier,
              height: maxHeight * professional_group?.total_app_logo_multiplier,
            });
          }
        } else {
          setDimensions({ width: naturalWidth * professional_group?.total_app_logo_multiplier, height: naturalHeight * professional_group?.total_app_logo_multiplier });
        }
      }
    };

    const img = imageRef.current;

    if (img && professional_group?.app_logo) {
      img.onload = handleResize;
      img.src = professional_group?.app_logo;
    }

    return () => {
      if (img) {
        img.onload = null;
      }
    };
  }, [professional_group?.app_logo, professional_group?.total_app_logo_multiplier]);

  return (
    <div>

        <Box sx={{ textAlign:'center', width:'100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          padding: 0,
          backgroundColor: 'transparent'
        }}>
          <img
            ref={imageRef}
            src={professional_group?.app_logo || undefined}
            style={{
              width: dimensions.width * 0.5,
              height: dimensions.height * 0.5,
              maxWidth: '80px',
              maxHeight: '80px',
              objectFit: 'contain',
              filter: 'grayscale(100%)',
              opacity: 0.5
            }}
            alt="Logo"
          />
        </Box>

        <Fade in key={stepIndex}>
          <Typography variant="body1" color="text.secondary">
            {analysisSteps[stepIndex]}
          </Typography>
        </Fade>
        </Box>
      </div>
  );
};

export default LogoLoader;
